import React, { useState } from 'react';
import { FileText, Download, Loader2 } from 'lucide-react';
import { generateQuestionPDF, generateTopicPDF, generateQuestionWithSolutionPDF, generateTopicWithSolutionsPDF } from '../utils/pdfGenerator';
import { generateQuestionHTMLPDF, generateTopicHTMLPDF, generateQuestionWithSolutionHTMLPDF, generateTopicWithSolutionsHTMLPDF } from '../utils/htmlToPdfGenerator';

const PDFControls = ({ 
  currentQuestion, 
  currentQuestionNumber, 
  allTopicQuestions, 
  topicInfo, 
  moduleInfo,
  className = "" 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);

  const handleGenerateQuestionPDF = async (withSolution = false, useHTML = false) => {
    if (!currentQuestion) return;
    
    setIsGenerating(true);
    try {
      if (useHTML) {
        // Use HTML2PDF method
        if (withSolution) {
          await generateQuestionWithSolutionHTMLPDF(currentQuestion, topicInfo, currentQuestionNumber, moduleInfo);
        } else {
          await generateQuestionHTMLPDF(currentQuestion, topicInfo, currentQuestionNumber, moduleInfo);
        }
      } else {
        // Use original jsPDF method
        if (withSolution) {
          generateQuestionWithSolutionPDF(currentQuestion, topicInfo, currentQuestionNumber, moduleInfo);
        } else {
          generateQuestionPDF(currentQuestion, topicInfo, currentQuestionNumber, moduleInfo);
        }
      }
    } catch (error) {
      console.error('Error generating question PDF:', error);
      alert('生成PDF時發生錯誤，請稍後再試');
    } finally {
      setIsGenerating(false);
      setShowOptions(false);
    }
  };

  const handleGenerateTopicPDF = async (withSolutions = false, useHTML = false) => {
    if (!allTopicQuestions || allTopicQuestions.length === 0) return;
    
    setIsGenerating(true);
    try {
      if (useHTML) {
        if (withSolutions) {
          await generateTopicWithSolutionsHTMLPDF(allTopicQuestions, topicInfo, moduleInfo);
        } else {
          await generateTopicHTMLPDF(allTopicQuestions, topicInfo, moduleInfo);
        }
      } else {
        if (withSolutions) {
          generateTopicWithSolutionsPDF(allTopicQuestions, topicInfo, moduleInfo);
        } else {
          generateTopicPDF(allTopicQuestions, topicInfo, moduleInfo);
        }
      }
    } catch (error) {
      console.error('Error generating topic PDF:', error);
      alert('生成PDF時發生錯誤，請稍後再試');
    } finally {
      setIsGenerating(false);
      setShowAllOptions(false);
    }
  };

  return (
    <div className={`pdf-controls ${className}`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          {/* PDF + Question Number Button */}
          <div className="relative">
            <button
              onClick={() => setShowOptions(!showOptions)}
              disabled={isGenerating || !currentQuestion}
              className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md text-sm"
            >
              {isGenerating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <FileText size={14} />
              )}
              <span className="font-medium">PDF</span>
              <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">
                {currentQuestionNumber}
              </span>
            </button>

            {/* Enhanced options dropdown for question PDF */}
            {showOptions && (
              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-64">
                <div className="p-2 border-b border-gray-100">
                  <div className="text-xs font-medium text-gray-600 mb-2">選擇生成方式:</div>
                </div>
                
                {/* Standard jsPDF options */}
                <div className="p-1">
                  <div className="text-xs text-gray-500 px-2 py-1">標準方式 (jsPDF):</div>
                  <button
                    onClick={() => handleGenerateQuestionPDF(false, false)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={14} />
                      <span className="text-sm">題目 {currentQuestionNumber}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">僅包含題目內容</div>
                  </button>
                  <button
                    onClick={() => handleGenerateQuestionPDF(true, false)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={14} />
                      <span className="text-sm">題目 {currentQuestionNumber} + 解答</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">包含詳細解答步驟</div>
                  </button>
                </div>

                {/* HTML2PDF options */}
                <div className="p-1 border-t border-gray-100">
                  <div className="text-xs text-gray-500 px-2 py-1">增強方式 (HTML2PDF) - 推薦:</div>
                  <button
                    onClick={() => handleGenerateQuestionPDF(false, true)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-blue-600" />
                      <span className="text-sm text-blue-800">題目 {currentQuestionNumber} (增強版)</span>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">更好的數學符號顯示</div>
                  </button>
                  <button
                    onClick={() => handleGenerateQuestionPDF(true, true)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText size={14} className="text-blue-600" />
                      <span className="text-sm text-blue-800">題目 {currentQuestionNumber} + 解答 (增強版)</span>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">包含解答 + 更好顯示</div>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* PDF + All Button with enhanced dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowAllOptions(!showAllOptions)}
              disabled={isGenerating || !allTopicQuestions || allTopicQuestions.length === 0}
              className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md text-sm"
            >
              {isGenerating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              <span className="font-medium">PDF</span>
              <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">
                All ({allTopicQuestions?.length || 0})
              </span>
            </button>
            
            {/* Enhanced dropdown for All PDF options */}
            {showAllOptions && (
              <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-72">
                <div className="p-2 border-b border-gray-100">
                  <div className="text-xs font-medium text-gray-600 mb-2">選擇生成方式:</div>
                </div>

                {/* Standard jsPDF options */}
                <div className="p-1">
                  <div className="text-xs text-gray-500 px-2 py-1">標準方式 (jsPDF):</div>
                  <button
                    onClick={() => handleGenerateTopicPDF(false, false)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Download size={14} />
                      <span className="text-sm">全部題目</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">僅包含題目，不含解答</div>
                  </button>
                  <button
                    onClick={() => handleGenerateTopicPDF(true, false)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Download size={14} />
                      <span className="text-sm">全部題目 + 詳細解答</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">包含題目和完整解答步驟</div>
                  </button>
                </div>

                {/* HTML2PDF options */}
                <div className="p-1 border-t border-gray-100">
                  <div className="text-xs text-gray-500 px-2 py-1">增強方式 (HTML2PDF) - 推薦:</div>
                  <button
                    onClick={() => handleGenerateTopicPDF(false, true)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Download size={14} className="text-blue-600" />
                      <span className="text-sm text-blue-800">全部題目 (增強版)</span>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">僅題目，更好的數學符號顯示</div>
                  </button>
                  <button
                    onClick={() => handleGenerateTopicPDF(true, true)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Download size={14} className="text-blue-600" />
                      <span className="text-sm text-blue-800 font-medium">全部題目 + 詳細解答 (增強版)</span>
                    </div>
                    <div className="text-xs text-blue-600 mt-1">包含完整解答 + 完美數學符號顯示</div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info text */}
        <div className="text-xs text-gray-500 mt-2 sm:mt-0 sm:ml-4">
          <div className="flex flex-col sm:flex-row gap-1 sm:gap-4">
            <span>📄 單題: 生成當前題目PDF</span>
            <span>📚 全部: 生成主題所有題目PDF (可選含解答)</span>
            <span className="text-blue-600">💡 推薦使用增強版本以獲得更好的數學符號顯示</span>
          </div>
        </div>
      </div>

      {/* Click outside to close dropdowns */}
      {(showOptions || showAllOptions) && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => {
            setShowOptions(false);
            setShowAllOptions(false);
          }}
        />
      )}
    </div>
  );
};

export default PDFControls; 